import React, { useState, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import TimelineIcon from "@mui/icons-material/Timeline";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import API_BASE_URL from "../../src/config";

const ApplyNowJob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const fileInputRef = useRef(null);

  const jobTitleFromState = location.state?.jobTitle || "";
  const jobIdFromState = location.state?.jobId || "";

  useEffect(() => {
    if (!jobTitleFromState || !jobIdFromState) {
      navigate("/jobs");
    }
  }, [jobTitleFromState, jobIdFromState, navigate]);

  const [applyJob, setApplyJob] = useState({
    title: jobTitleFromState,
    description: "",
    experience: "",
    image_url: "",
    resume: null,
  });

  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    if (e.target.type === "file") {
      const file = e.target.files[0];
      if (file) {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        if (!allowedTypes.includes(file.type)) {
          setErrorMessage("Unsupported file type. Please upload PDF, DOC, or DOCX.");
          return;
        }
        setErrorMessage("");
        setApplyJob({ ...applyJob, resume: file });
      }
    } else {
      setApplyJob({ ...applyJob, [e.target.name]: e.target.value });
    }
  };

  const resetForm = () => {
    setApplyJob({
      title: "",
      description: "",
      experience: "",
      image_url: "",
      resume: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!applyJob.resume) {
      setErrorMessage("Please upload your resume.");
      return;
    }

    const formData = new FormData();
    formData.append("job_title", applyJob.title);
    formData.append("name", user?.name);
    formData.append("message", applyJob.description);
    formData.append("experience", applyJob.experience);
    formData.append("user_id", user?.id);
    formData.append("job_id", jobIdFromState);
    formData.append("image_url", applyJob.image_url);
    formData.append("resume", applyJob.resume);

    setSubmitted(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/applynowjob`, formData)

      setSuccessMessage("Successfully applied for the job!");
      resetForm();
    } catch (error) {
      setErrorMessage("Failed to apply. Please try again.");
    } finally {
      setSubmitted(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h5" color="#42326e" fontWeight="bold" textAlign="center" mb={5}>
            Apply for Job {applyJob.title}
          </Typography>

          {/* Success & Error Messages */}
          {successMessage && (
            <Snackbar open autoHideDuration={4000} onClose={() => setSuccessMessage("")}>
              <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
          )}
          {errorMessage && (
            <Snackbar open autoHideDuration={4000} onClose={() => setErrorMessage("")}>
              <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
          )}

          <form onSubmit={handleSubmit}>
            {/* Job Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <WorkIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                label="Job Title"
                variant="outlined"
                name="title"
                value={applyJob.title}
                disabled
              />
            </Box>

            {/* User Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <TitleIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField fullWidth label="Your Name" variant="outlined" value={user?.name || ""} disabled />
            </Box>

            {/* Description */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DescriptionIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                label="Message"
                variant="outlined"
                name="description"
                value={applyJob.description}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Box>

            {/* Experience */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <TimelineIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                label="Experience (in years)"
                variant="outlined"
                name="experience"
                type="number"
                value={applyJob.experience}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.1" }}
                required
              />
            </Box>

            {/* Image URL */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <ImageIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                label="Any ID Proof or Certificates (Optional)"
                variant="outlined"
                name="image_url"
                value={applyJob.image_url}
                onChange={handleChange}
              />
            </Box>

            {/* Resume Upload */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <UploadFileIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Upload Resume (PDF, DOC, DOCX only) <span style={{ color: "red" }}>*</span>
                </Typography>
                <Button variant="outlined" component="label" fullWidth>
                  {applyJob.resume ? applyJob.resume.name : "Choose File"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleChange}
                  />
                </Button>
              </Box>
            </Box>

            {/* Submit Button */}
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{
                mt: 3,
                py: 1,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                },
              }}
              type="submit"
              disabled={submitted}
            >
              {submitted ? <CircularProgress size={24} color="inherit" /> : "Submit"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ApplyNowJob;
